import { FormControl, InputLabel, MenuItem, Select, Stack, styled } from '@mui/material';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';

import { getSelectedChildFilters } from './ProductFilter.utils';

type Options = { label: string; value: string; imageUrl?: string; filter?: Filter }[];

export type Filter = {
  id: string;
  label: string;
  options: Options;
};

export type Filters = Map<string, string>;

export interface ProductFilterProps extends PropsWithChildren {
  selectedFilters?: Filters;
  productFilter: Filter;
  onChange: (filters: Filters) => void;
  additionalSlot?: boolean;
}

type ChildPropDataFilter = { props: { 'data-filter': Filter } };

const StyledImage = styled('img')({
  width: '1rem',
  height: '1rem',
  marginRight: '0.5rem',
});

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'additionalSlot',
})<{ additionalSlot?: boolean }>(({ theme, additionalSlot }) => ({
  width: '100%',
  [theme.breakpoints.up('lg')]: {
    width: additionalSlot ? '24%' : '32.333%',
  },
}));
/**
 * Typically used when creating a prescription to filter the medication
 * based on the predefined filters
 *
 * @param selectedFilters - selected filters in key value pairs
 * @param productFilter - nested object that contains a chain of filter values
 * @param onChange - callback fired when a filter is selected from a list
 *
 * @returns JSXElement
 *
 */

export default function ProductFilter({
  selectedFilters,
  productFilter,
  onChange,
  additionalSlot,
  children,
}: ProductFilterProps) {
  const selectedFiltersByKey = selectedFilters && Array.from(selectedFilters.keys());
  const initialFilterKey = selectedFiltersByKey?.[0] || productFilter.id;
  const [childFilters, setChildFilters] = useState<Filter[]>([]);
  const [filter, setFilter] = useState<Filters>(selectedFilters ?? new Map<string, string>([[initialFilterKey, '']]));

  useEffect(() => {
    if (selectedFilters) {
      const selectedChildFilters = getSelectedChildFilters(productFilter, Array.from(selectedFilters.values()));
      setChildFilters(selectedChildFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (key: string, val: string, childFilter?: Filter) => {
    const isNewFormulation = initialFilterKey === key && filter.get(key) !== val;

    let newFilter: Filters;
    let newChildFilters: Filter[];

    if (isNewFormulation) {
      newFilter = new Map([[initialFilterKey, val]]);
      newChildFilters = childFilter ? [childFilter] : [];
    } else if (childFilter) {
      const tooManyChildFilters = childFilters.length > 1;
      if (tooManyChildFilters) {
        newFilter = new Map([
          [initialFilterKey, filter.get(initialFilterKey) || ''],
          [key, val],
        ]);
        const trimmed = childFilters.slice(0, -1);
        newChildFilters = trimmed.find((c) => c.id === childFilter.id) ? trimmed : [...trimmed, childFilter];
      } else {
        newFilter = new Map([...filter, [key, val]]);
        newChildFilters = childFilters.find((c) => c.id === childFilter.id)
          ? [...childFilters]
          : [...childFilters, childFilter];
      }
    } else {
      newFilter = new Map([...filter, [key, val]]);
      newChildFilters = childFilters;
    }

    setFilter(newFilter);
    setChildFilters(newChildFilters);
    onChange(newFilter);
  };

  return (
    <Stack direction={{ md: 'column', lg: 'row' }} spacing={2}>
      <StyledFormControl size="small" additionalSlot={additionalSlot}>
        <InputLabel id={`${initialFilterKey}-label`} data-dd-privacy="allow">
          {productFilter.label}
        </InputLabel>
        <Select
          label={productFilter.label}
          labelId={`${initialFilterKey}-label`}
          onChange={(e, child) => {
            handleChange(productFilter.id, e.target.value, (child as ChildPropDataFilter)?.props?.['data-filter']);
          }}
          value={filter.get(initialFilterKey) || ''}
          size="small"
          data-dd-privacy="allow"
        >
          {(productFilter.options || []).map(({ label, value, imageUrl, filter: childFilter }) => (
            <MenuItem key={value} value={value} data-filter={childFilter}>
              {imageUrl && <StyledImage src={imageUrl} height={16} />}
              {label}
            </MenuItem>
          ))}
        </Select>
      </StyledFormControl>

      {(childFilters || []).map((childFilter) => (
        <StyledFormControl key={childFilter.id} size="small" additionalSlot={additionalSlot}>
          <InputLabel id={`${childFilter.id}-label`} data-dd-privacy="allow">
            {childFilter.label}
          </InputLabel>
          <Select
            label={childFilter.label}
            labelId={`${childFilter.id}-label`}
            onChange={(e, child) =>
              handleChange(childFilter.id, e.target.value, (child as ChildPropDataFilter)?.props?.['data-filter'])
            }
            value={filter?.get(childFilter.id) || ''}
            size="small"
            data-dd-privacy="allow"
          >
            {(childFilter.options || []).map(({ label, value, imageUrl, filter: anotherchildFilter }) => (
              <MenuItem key={value} value={value} data-filter={anotherchildFilter}>
                {imageUrl && <StyledImage src={imageUrl} height={16} />}
                {label}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>
      ))}

      {children}
    </Stack>
  );
}
